import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.scss';

/** Hover dwell before showing, so sweeping across a table doesn't strobe. */
const SHOW_DELAY_MS = 120;
/** Keep the bubble this far inside the viewport edges. */
const VIEWPORT_MARGIN = 8;
/** Gap between the bubble and the element it describes. */
const ANCHOR_GAP = 8;

type TooltipTarget = {
  content: React.ReactNode;
  anchor: HTMLElement;
};

type Placement = 'top' | 'bottom';

type Position = {
  top: number;
  left: number;
  placement: Placement;
};

/**
 * A single shared tooltip for a subtree — one piece of state and one portal,
 * however many triggers there are. Tables render thousands of cells, so giving
 * each one its own tooltip instance would be wasteful.
 *
 * Render `tooltip` once, then call `show`/`hide` from any element's pointer and
 * focus handlers.
 */
export function useTooltip() {
  const [target, setTarget] = useState<TooltipTarget | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    cancelPending();
    setTarget(null);
  }, [cancelPending]);

  const show = useCallback(
    (content: React.ReactNode, anchor: HTMLElement | null) => {
      cancelPending();

      // Empty content is the common case (a cell that isn't truncated and has
      // no description), so bail before scheduling anything.
      if (content === null || content === undefined || content === '' || !anchor) {
        return;
      }

      timerRef.current = setTimeout(() => setTarget({ content, anchor }), SHOW_DELAY_MS);
    },
    [cancelPending]
  );

  useEffect(() => cancelPending, [cancelPending]);

  // The bubble is positioned against a rect captured at show time, so any
  // scroll or resize leaves it pointing at the wrong place. Dismiss instead of
  // chasing the anchor. Capture phase catches scrolling inside `.table-scroll`.
  useEffect(() => {
    if (!target) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };

    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [target, hide]);

  const tooltip = target ? <TooltipBubble content={target.content} anchor={target.anchor} /> : null;

  return { show, hide, tooltip };
}

function TooltipBubble({ content, anchor }: TooltipTarget) {
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position | null>(null);

  // Two-pass: render offscreen to measure, then place. The bubble wraps, so its
  // height isn't knowable before layout.
  useLayoutEffect(() => {
    const bubble = bubbleRef.current;
    if (!bubble) return;

    const anchorRect = anchor.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();

    const fitsAbove = anchorRect.top - bubbleRect.height - ANCHOR_GAP >= VIEWPORT_MARGIN;
    const placement: Placement = fitsAbove ? 'top' : 'bottom';

    const top = fitsAbove
      ? anchorRect.top - bubbleRect.height - ANCHOR_GAP
      : anchorRect.bottom + ANCHOR_GAP;

    const centeredLeft = anchorRect.left + anchorRect.width / 2 - bubbleRect.width / 2;
    const rightBound = Math.max(
      VIEWPORT_MARGIN,
      window.innerWidth - bubbleRect.width - VIEWPORT_MARGIN
    );
    const left = Math.min(Math.max(VIEWPORT_MARGIN, centeredLeft), rightBound);

    setPosition({ top, left, placement });
  }, [content, anchor]);

  return createPortal(
    <div
      ref={bubbleRef}
      role="tooltip"
      className={`tooltip ${position ? `tooltip--${position.placement}` : 'tooltip--measuring'}`}
      style={position ? { top: position.top, left: position.left } : undefined}
    >
      {content}
    </div>,
    document.body
  );
}

/**
 * Text shown when hovering a table cell whose content is visually clipped.
 *
 * A `<select>`'s textContent is every option concatenated, so read the selected
 * one instead — otherwise the status pill would report the whole status list.
 */
export function readTruncatedText(cell: HTMLElement): string {
  const select = cell.querySelector('select');
  if (select) {
    return select.selectedOptions[0]?.text?.trim() ?? '';
  }

  return (cell.innerText || cell.textContent || '').trim();
}

/**
 * Whether anything inside a cell is being clipped horizontally.
 *
 * The `<td>` itself rarely overflows — the ellipsis lives on an inner element
 * (`.prod-col__name`, the status pill), so descendants have to be checked too.
 * The 1px tolerance absorbs sub-pixel layout rounding.
 */
export function isCellTruncated(cell: HTMLElement): boolean {
  if (cell.scrollWidth > cell.clientWidth + 1) return true;

  return Array.from(cell.querySelectorAll<HTMLElement>('*')).some(
    (element) => element.scrollWidth > element.clientWidth + 1
  );
}
